import { createSelector } from 'reselect';

import { uniqBy } from 'modules/utils/ArrayUtils';

import { RootState } from 'store/types';
import * as myDoctorsSelectors from 'store/myDoctors/selectors';
import { isBookableFilter, mapDoctorToType } from 'store/booking/mappers';

export const appointmentDetailsSelector = (state: RootState) =>
  state.booking.appointment.appointmentDetails;

export const currentPatientIdSelector = createSelector(
  appointmentDetailsSelector,
  appointmentDetails => appointmentDetails.patient?.consumerId
);

export const currentCernerPersonIdSelector = createSelector(
  appointmentDetailsSelector,
  appointmentDetails => appointmentDetails.patient?.cernerPersonId
);

export const currentProviderIdSelector = createSelector(
  appointmentDetailsSelector,
  appointmentDetails => appointmentDetails.doctor?.providerId
);

export const currentResourceIdProviderSelector = createSelector(
  appointmentDetailsSelector,
  appointmentDetails => appointmentDetails.doctor?.cernerResourceId
);

export const currentProviderSelector = createSelector(
  appointmentDetailsSelector,
  appointmentDetails => appointmentDetails.doctor
);

export const fetchSlotsSelector = createSelector(
  appointmentDetailsSelector,
  appointmentDetails => appointmentDetails.fetchSlots
);

export const updatedVisitGroupHasBeenSelected = createSelector(
  appointmentDetailsSelector,
  appointmentDetails => appointmentDetails.updatedVisitGroup
);

export const appointmentDetailsFetchingSelector = createSelector(
  appointmentDetailsSelector,
  appointmentDetails => appointmentDetails.isFetching
);

export const appointmentDetailsErrorSelector = createSelector(
  appointmentDetailsSelector,
  appointmentDetails => appointmentDetails.error
);

export const groupedVisitTypeSelector = createSelector(
  appointmentDetailsSelector,
  appointmentDetails => appointmentDetails.groupedVisitType
);

export const appointmentTypeSelector = createSelector(
  appointmentDetailsSelector,
  appointmentDetails => appointmentDetails.appointmentType
);

export const isExternalRequestSelector = createSelector(
  appointmentDetailsSelector,
  appointmentDetails => appointmentDetails.isExternalRequest
);

export const visitTypeSelector = createSelector(
  [appointmentTypeSelector, groupedVisitTypeSelector],
  (appointmentType, groupedVisitType) => {
    if (appointmentType) {
      const { visitTypes } = groupedVisitType || { visitTypes: [] };

      return visitTypes.find(type => type.appointmentType === appointmentType);
    }

    return null;
  }
);

export const appointmentLocationSelector = createSelector(
  appointmentDetailsSelector,
  appointmentDetails => appointmentDetails.appointmentLocation
);

export const groupedVisitTypeLocationsSelector = createSelector(
  [appointmentTypeSelector, groupedVisitTypeSelector],
  (appointmentType, groupedVisitType) => {
    if (appointmentType) {
      const { visitTypes } = groupedVisitType || { visitTypes: [] };
      const visitType = visitTypes.find(type => type.appointmentType === appointmentType);

      if (visitType) {
        return visitType.locations;
      }
    }

    return [];
  }
);

export const appointmentDateSelector = (state: RootState) =>
  state.booking.appointment.appointmentDetails.appointmentDate;
export const appointmentTimeSelector = (state: RootState) =>
  state.booking.appointment.appointmentDetails.appointmentTime;

export const appointmentTimeForLocationSelector = createSelector(
  [appointmentTimeSelector, groupedVisitTypeLocationsSelector],
  (timeSlot, locations) => {
    if (!timeSlot || !locations) {
      return null;
    }

    return locations.find(location => location.locationId === timeSlot.locationId);
  }
);

export const lockedAppointmentSelector = createSelector(
  appointmentDetailsSelector,
  appointmentDetails => appointmentDetails.lockedAppointment
);

export const lockedAppointmentIsLockingSelector = createSelector(
  appointmentDetailsSelector,
  appointmentDetails => appointmentDetails.isLockingAppointment
);

export const isRescheduleSelector = createSelector(
  [appointmentDetailsSelector],
  appointmentDetails => appointmentDetails.isEditingAppointment
);

export const allProvidersSelector = createSelector(
  [myDoctorsSelectors.pcpDoctorsSelector, myDoctorsSelectors.pastDoctorsSelector],
  (pcp, past) => [...pcp, ...past]
);

export const bookableProvidersWithTypeSelector = createSelector(
  [myDoctorsSelectors.pcpDoctorsSelector, myDoctorsSelectors.pastDoctorsSelector],
  (pcp, preferred, past) => {
    return uniqBy(
      [
        ...pcp.map(dr => mapDoctorToType(dr, 'isPcp')),
        ...past.map(dr => mapDoctorToType(dr, 'isPast'))
      ],
      'cernerResourceId'
    ).filter(isBookableFilter);
  }
);

export const currentPatientPcpDoctorsSelector = createSelector(
  [
    myDoctorsSelectors.pcpDoctorConsumerSelector,
    myDoctorsSelectors.doctorSummaryListSelector,
    currentPatientIdSelector
  ],
  myDoctorsSelectors.getDoctorListForSelector
);

export const currentPatientPastDoctorsSelector = createSelector(
  [
    myDoctorsSelectors.pastDoctorConsumerSelector,
    myDoctorsSelectors.doctorSummaryListSelector,
    currentPatientIdSelector
  ],
  myDoctorsSelectors.getDoctorListForSelector
);

export const currentPatientAllDoctorsSelector = createSelector(
  [currentPatientPcpDoctorsSelector, currentPatientPastDoctorsSelector],
  (pcp, past) => uniqBy([...pcp, ...past], 'cernerResourceId')
);

export const currentPatientBookingDoctorsSelector = createSelector(
  currentPatientAllDoctorsSelector,
  doctors => doctors.filter(d => d.isAcceptingOnlineBooking === true)
);

export const selectedSpecialtySelector = createSelector(
  appointmentDetailsSelector,
  appointmentDetails => appointmentDetails.specialty
);
